"use client";

import React from "react";
import { ArrowRight, BookOpen, ChevronRight, CircleCheck as CheckCircle2, Cpu, FlaskConical, Leaf, Recycle, ShieldCheck, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const keyPillars = [{
  id: 1,
  title: "Up to 90% Solvent Recovery",
  description: "Closed-loop solvent recycling engineered for high-throughput HPLC systems.",
  iconName: "recycle"
}, {
  id: 2,
  title: "Full GMP & Pharmacopeia Compliance",
  description: "Method validation without compromising chromatographic resolution or purity.",
  iconName: "shield"
}, {
  id: 3,
  title: "Carbon & Cost Reduction",
  description: "Measurable lifecycle reductions in hazardous waste disposal and transport.",
  iconName: "leaf"
}];
const fastMetrics = [{
  label: "Solvent Waste Cut",
  value: "85-90%"
}, {
  label: "Method Integrity Focus",
  value: "Validated"
}, {
  label: "Payback Period",
  value: "< 14 Mos"
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "recycle":
        return <Recycle className="size-5 text-primary" />;
      case "shield":
        return <ShieldCheck className="size-5 text-primary" />;
      case "leaf":
      default:
        return <Leaf className="size-5 text-primary" />;
    }
  };
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background pt-8 pb-20 md:pt-12 md:pb-28 border-b border-border">
      {/* Subtle background ambient patterns */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary)/0.08,transparent_55%)]" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-secondary/80 blur-3xl" aria-hidden="true" />

      <div className="page-container relative z-10">
        {/* Breadcrumb Navigation */}
        <nav aria-label="breadcrumb" className="mb-6">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-muted-foreground">
            <li className="inline-flex items-center gap-1.5">
              <Link to="/HomePage" className="transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <Link to="/ResourcesPage" className="transition-colors hover:text-foreground">
                Resources
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <span aria-current="page" className="font-medium text-foreground">
                HPLC Sustainability Knowledge Centre
              </span>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Main Hero Copy & Actions */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <div className="inline-flex items-center gap-2 mb-6">
              <Badge variant="secondary" className="px-3 py-1 rounded-full text-xs font-semibold gap-1.5 border border-border">
                <Leaf className="size-3.5 text-primary" />
                <span>HPLC Sustainability Knowledge Hub</span>
              </Badge>
              <span className="inline-flex items-center text-xs font-medium text-muted-foreground">
                <Sparkles className="size-3 text-primary mr-1" />
                Antech Technical Resource
              </span>
            </div>

            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl leading-[1.15]">
              Sustainable Chromatography for Regulated Laboratories
            </h1>

            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
              Practical strategies, technical benchmarks, and qualification roadmaps for reducing solvent waste, cutting operational carbon, and adopting high-efficiency HPLC technologies across Ireland and UK facilities.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="rounded-md font-medium shadow-sm hover:shadow">
                <Link to="/HplcSolventRecoveryResolvePage" className="inline-flex items-center gap-2">
                  <span>Explore ReSolve Recovery</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/ResourcesPage" className="inline-flex items-center gap-2">
                  <BookOpen className="size-4 text-muted-foreground" />
                  <span>Technical Papers & Guides</span>
                </Link>
              </Button>
            </div>

            {/* Credibility metric ribbon */}
            <div className="mt-10 pt-8 border-t border-border w-full grid grid-cols-3 gap-4">
              {fastMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col">
                  <span className="text-xl md:text-2xl font-bold font-default text-foreground">
                    {metric.value}
                  </span>
                  <span className="text-xs text-muted-foreground mt-0.5">
                    {metric.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Interactive Feature Visual & Hub Card */}
          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="relative">
              <Card className="border border-border shadow-md bg-card overflow-hidden">
                <div className="relative h-48 w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hplc_laboratory_sustainability_overview.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hplc_laboratory_sustainability_overview.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hplc_laboratory_sustainability_overview.webp-wvc-sizes" alt="Modern high performance liquid chromatography laboratory instrument with green sustainability indicators" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-linear-to-t from-card via-card/20 to-transparent" />
                  <div className="absolute top-3 right-3">
                    <Badge variant="default" className="bg-primary text-primary-foreground shadow-xs font-medium">
                      Live Hub Topics
                    </Badge>
                  </div>
                </div>

                <CardContent className="pt-5 pb-6 flex flex-col gap-4">
                  <div className="flex items-center justify-between pb-3 border-b border-border">
                    <div className="flex items-center gap-2 text-foreground font-medium text-sm">
                      <FlaskConical className="size-4 text-primary" />
                      <span>Analytical Sustainability Focus</span>
                    </div>
                    <span className="text-xs text-muted-foreground font-mono">Q1/Q2 Guidance</span>
                  </div>

                  <div className="space-y-3">
                    {keyPillars.map((pillar, i) => <div key={pillar.id} data-index={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-secondary/50 border border-border/60 transition-[background-color,border-color] hover:bg-secondary hover:border-border">
                        <div className="mt-0.5 rounded-md bg-card p-1.5 shadow-2xs border border-border shrink-0">
                          {getIcon(pillar.iconName)}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-foreground leading-tight">
                            {pillar.title}
                          </span>
                          <span className="text-xs text-muted-foreground mt-1 leading-snug">
                            {pillar.description}
                          </span>
                        </div>
                      </div>)}
                  </div>

                  <div className="mt-2 pt-3 border-t border-border flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <CheckCircle2 className="size-3.5 text-primary" />
                      <span>Validated for Isocratic & Gradient Modes</span>
                    </div>

                    <Button asChild variant="ghost" size="sm" className="h-8 px-2 text-xs text-primary font-medium">
                      <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-1">
                        <span>Consult Team</span>
                        <ArrowRight className="size-3" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Decorative floating tech badge */}
              <div className="absolute -bottom-4 -left-4 hidden sm:flex items-center gap-2 rounded-lg bg-card border border-border px-3.5 py-2 shadow-md">
                <Cpu className="size-4 text-primary" />
                <span className="text-xs font-medium text-foreground">
                  PAT & Online Monitoring Ready
                </span>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}